import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { motion, AnimatePresence } from "framer-motion";
import EngagementsPanel from "../components/EngagementsPanel";
import { useOrg } from "../contexts/OrgProvider";
import { FRAMEWORKS as ALL_FRAMEWORKS, FW_SHORT, FW_COLORS } from "../constants/frameworks";
import {
  Plus, Search, Filter, Trash2, Edit2, AlertTriangle, Server,
  Database, Users, Cog, FileText, ChevronRight, X
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { StatusBadge, TEXT_TONE, BG_TONE_10, DOT } from "../components/ui/status-badge";
import { getStatusTone } from "../lib/statusTokens";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { Slider } from "../components/ui/slider";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from "../components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter
} from "../components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger
} from "../components/ui/alert-dialog";
import { extractError } from "../lib/errors";
import { toast } from "sonner";
import { TableSkeleton } from "../components/skeletons";
import { EmptyState } from "../components/EmptyState";
import { PageHeader } from "../components/PageHeader";

const RiskRegister = () => {
  const { token, user } = useAuth();
  const { t, locale } = useI18n();
  const { activeFrameworks } = useOrg();
  const orgFrameworks = ALL_FRAMEWORKS.filter((f) => activeFrameworks.includes(f.id));
  const [risks, setRisks] = useState([]);
  const [assets, setAssets] = useState([]);
  const [threats, setThreats] = useState([]);
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("all");
  const [showAddRisk, setShowAddRisk] = useState(false);
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);

  const [catalogItems, setCatalogItems] = useState([]);
  const [catalogTemplateApplied, setCatalogTemplateApplied] = useState(false);

  const [newRisk, setNewRisk] = useState({
    asset_id: "",
    threat_id: "",
    vulnerability_id: "",
    title: "",
    description: "",
    likelihood: 3,
    impact: 3,
    treatment: "mitigate",
    owner: "",
    due_date: "",
    // ISO 31000 / ISO 27005
    risk_category: "",
    risk_source: "",
    impact_confidentiality: 3,
    impact_integrity: 3,
    impact_availability: 3,
    existing_controls: "",
    residual_likelihood: null,
    residual_impact: null,
    risk_appetite: "",
    review_frequency: "",
    relevant_frameworks: []
  });

  const [newAsset, setNewAsset] = useState({
    name: "",
    asset_type: "software",
    description: "",
    confidentiality: 3,
    integrity: 3,
    availability: 3,
    owner: ""
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [risksRes, assetsRes, threatsRes, vulnsRes] = await Promise.all([
        axios.get(`${API}/risks`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/assets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/threats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/vulnerabilities`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setRisks(risksRes.data);
      setAssets(assetsRes.data);
      setThreats(threatsRes.data);
      setVulnerabilities(vulnsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load risk data");
    } finally {
      setLoading(false);
    }
  };

  const handleAddAsset = async () => {
    try {
      await axios.post(`${API}/assets`, newAsset, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Asset added successfully");
      setShowAddAsset(false);
      setNewAsset({
        name: "",
        asset_type: "software",
        description: "",
        confidentiality: 3,
        integrity: 3,
        availability: 3,
        owner: ""
      });
      fetchData();
    } catch (error) {
      toast.error(extractError(error, "Failed to add asset"));
    }
  };

  const handleAddRisk = async () => {
    if (!newRisk.title || !newRisk.asset_id) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      await axios.post(`${API}/risks`, newRisk, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Risk added successfully");
      setShowAddRisk(false);
      resetNewRisk();
      fetchData();
    } catch (error) {
      toast.error(extractError(error, "Failed to add risk"));
    }
  };

  const handleUpdateRisk = async () => {
    if (!editingRisk) return;

    try {
      await axios.put(`${API}/risks/${editingRisk.risk_id}`, {
        asset_id: editingRisk.asset_id,
        threat_id: editingRisk.threat_id,
        vulnerability_id: editingRisk.vulnerability_id,
        title: editingRisk.title,
        description: editingRisk.description,
        likelihood: editingRisk.likelihood,
        impact: editingRisk.impact,
        treatment: editingRisk.treatment,
        owner: editingRisk.owner,
        due_date: editingRisk.due_date
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Risk updated successfully");
      setEditingRisk(null);
      fetchData();
    } catch (error) {
      toast.error(extractError(error, "Failed to update risk"));
    }
  };

  const handleDeleteRisk = async (riskId) => {
    try {
      await axios.delete(`${API}/risks/${riskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Risk deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete risk");
    }
  };

  const resetNewRisk = () => {
    setNewRisk({
      asset_id: "",
      threat_id: "",
      vulnerability_id: "",
      title: "",
      description: "",
      likelihood: 3,
      impact: 3,
      treatment: "mitigate",
      owner: "",
      due_date: "",
      risk_category: "",
      risk_source: "",
      impact_confidentiality: 3,
      impact_integrity: 3,
      impact_availability: 3,
      existing_controls: "",
      residual_likelihood: null,
      residual_impact: null,
      risk_appetite: "",
      review_frequency: "",
      relevant_frameworks: []
    });
    setCatalogTemplateApplied(false);
  };

  // Fetch risk catalog templates for all active frameworks when the "Add Risk" sheet opens.
  const fetchCatalog = async () => {
    if (orgFrameworks.length === 0) return;
    try {
      const results = await Promise.allSettled(
        orgFrameworks.map((f) =>
          axios.get(`${API}/frameworks/${f.id}/templates?locale=${locale || "en"}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );
      const combined = [];
      results.forEach((res, idx) => {
        if (res.status === "fulfilled") {
          const fw = orgFrameworks[idx];
          (res.value.data.suggested_risks || []).forEach((risk) => {
            combined.push({ ...risk, _framework: fw.id, _framework_short: fw.short });
          });
        }
      });
      setCatalogItems(combined);
    } catch {
      // Catalog is optional — silent failure is acceptable.
    }
  };

  const applyTemplate = (catalogId) => {
    const item = catalogItems.find((c) => c.id === catalogId);
    if (!item) return;
    setNewRisk((prev) => ({
      ...prev,
      risk_category: item.category || prev.risk_category,
      risk_source: item.source || prev.risk_source,
      description: item.description || prev.description,
      threat_actor: item.threat_actor || "",
      existing_controls: item.mitigating_control
        ? `${item.mitigating_control} — ${item.mitigating_control_title}`
        : prev.existing_controls,
    }));
    setCatalogTemplateApplied(true);
  };

  const getAssetIcon = (type) => {
    switch (type) {
      case "hardware": return Server;
      case "software": return Cog;
      case "data": return Database;
      case "people": return Users;
      default: return FileText;
    }
  };

  const filteredRisks = risks.filter(risk => {
    const matchesSearch = risk.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      risk.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLevel = selectedLevel === "all" || risk.risk_level === selectedLevel;
    return matchesSearch && matchesLevel;
  });

  const riskMatrix = Array.from({ length: 5 }, (_, i) =>
    Array.from({ length: 5 }, (_, j) => {
      const likelihood = 5 - i;
      const impact = j + 1;
      const score = likelihood * impact;
      let level = "low";
      if (score >= 20) level = "critical";
      else if (score >= 15) level = "high";
      else if (score >= 10) level = "medium";
      return { likelihood, impact, score, level };
    })
  );

  if (loading) {
    return <TableSkeleton rows={8} columns={6} label="Loading risk register" />;
  }

  return (
    <div className="p-6 space-y-6" data-testid="risk-register">
      <PageHeader
        title={t("risks.title")}
        description={t("risks.subtitle")}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowAddAsset(true)} data-testid="add-asset-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t("risks.add_asset")}
            </Button>
            <Button onClick={() => { setShowAddRisk(true); fetchCatalog(); }} data-testid="add-risk-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t("risks.add_risk")}
            </Button>
          </div>
        }
      >
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">ISO 31000</Badge>
          <Badge variant="outline" className="text-xs">ISO 27005</Badge>
        </div>
      </PageHeader>

      {/* Client Engagements Panel */}
      <EngagementsPanel
        assessmentType="risk"
        runEndpoint="run-risk"
        titleKey="risks.assessments"
        newButtonKey="risks.new_assessment"
      />

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{risks.length}</p>
                <p className="text-xs text-muted-foreground">{t("ux.total_risks")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {["critical", "high", "medium", "low"].map(level => {
          const count = risks.filter(r => r.risk_level === level).length;
          const tone = getStatusTone("risk", level);
          return (
            <Card
              key={level}
              className={`border-border cursor-pointer transition-all ${selectedLevel === level ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setSelectedLevel(selectedLevel === level ? "all" : level)}
            >
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${BG_TONE_10[tone]} ${TEXT_TONE[tone]}`}>
                    <span className="text-lg font-bold">{count}</span>
                  </div>
                  <p className="text-sm font-medium">{t(level)}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Risk Matrix */}
      <Card className="border-border" data-testid="risk-matrix">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">{t("risks.matrix")}</CardTitle>
          <CardDescription>5x5 Likelihood vs Impact Matrix</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-1">
            {/* Y-axis vertical label — contained in a fixed-width relative+overflow-hidden
                wrapper so the rotated text can't overflow onto neighboring content
                (mirrors RiskHeatMap.jsx's Y-axis label pattern). */}
            <div className="relative overflow-hidden flex-shrink-0" style={{ width: "1rem" }}>
              <span
                className="text-xs text-muted-foreground whitespace-nowrap"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%) rotate(-90deg)",
                  transformOrigin: "center center",
                }}
              >
                {t("risks.likelihood")}
              </span>
            </div>
            <div className="flex flex-col justify-around pr-2 text-xs text-muted-foreground">
              {[5, 4, 3, 2, 1].map(l => (
                <span key={l} className="h-10 flex items-center">{l}</span>
              ))}
            </div>
            <div className="flex-1">
              <div className="grid grid-cols-5 gap-1">
                {riskMatrix.flat().map((cell, idx) => {
                  const riskCount = risks.filter(r =>
                    r.likelihood === cell.likelihood && r.impact === cell.impact
                  ).length;
                  return (
                    <div
                      key={idx}
                      className={`h-10 rounded flex items-center justify-center text-xs font-medium text-white ${DOT[getStatusTone("risk", cell.level)]}`}
                    >
                      {riskCount > 0 ? riskCount : ""}
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-around mt-2 text-xs text-muted-foreground">
                {[1, 2, 3, 4, 5].map(i => (
                  <span key={i}>{i}</span>
                ))}
              </div>
              <div className="text-center text-xs text-muted-foreground mt-1">{t("risks.impact")}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t("search") + "..."}
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="risk-search-input"
          />
        </div>
        {selectedLevel !== "all" && (
          <Button variant="ghost" size="sm" onClick={() => setSelectedLevel("all")}>
            Clear Filter <X className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>

      {/* Risks List */}
      <ScrollArea className="h-[calc(100vh-36rem)]">
        <div className="space-y-3 pr-4">
          <AnimatePresence mode="popLayout">
            {filteredRisks.map((risk, index) => {
              const asset = assets.find(a => a.asset_id === risk.asset_id);
              const AssetIcon = asset ? getAssetIcon(asset.asset_type) : FileText;

              return (
                <motion.div
                  key={risk.risk_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2, delay: index * 0.02 }}
                >
                  <Card className="border-border hover:border-primary/50 transition-colors" data-testid={`risk-card-${risk.risk_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <StatusBadge kind="risk" value={risk.risk_level} className="uppercase" />
                            <span className="text-sm text-muted-foreground">
                              Score: {risk.risk_score}
                            </span>
                            <Badge variant="secondary" className="capitalize">
                              {risk.treatment}
                            </Badge>
                          </div>
                          <h3 className="font-semibold mb-1">{risk.title}</h3>
                          {risk.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                              {risk.description}
                            </p>
                          )}
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            {asset && (
                              <span className="flex items-center gap-1">
                                <AssetIcon className="w-4 h-4" />
                                {asset.name}
                              </span>
                            )}
                            <span>L:{risk.likelihood} × I:{risk.impact}</span>
                            {risk.residual_score && (
                              <span className="text-xs">Residual: {risk.residual_score}</span>
                            )}
                            {risk.risk_category && (
                              <Badge variant="secondary" className="capitalize text-xs">{risk.risk_category}</Badge>
                            )}
                            {(risk.relevant_frameworks || []).map((fwId) => (
                              <Badge
                                key={fwId}
                                variant="outline"
                                className="text-[10px]"
                                style={{ borderColor: FW_COLORS[fwId], color: FW_COLORS[fwId] }}
                              >
                                {FW_SHORT[fwId] || fwId}
                              </Badge>
                            ))}
                            {risk.owner && <span>Owner: {risk.owner}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingRisk(risk)}
                            data-testid={`edit-risk-${risk.risk_id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`delete-risk-${risk.risk_id}`}>
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>{t("risks.delete_confirm_title")}</AlertDialogTitle>
                                <AlertDialogDescription>
                                  {t("risks.delete_confirm_desc")}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeleteRisk(risk.risk_id)}>
                                  {t("delete")}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>

          {filteredRisks.length === 0 && (
            <EmptyState
              icon={AlertTriangle}
              title={searchQuery || selectedLevel !== "all"
                ? t("risks.no_risks_filter")
                : t("risks.no_risks")}
              description={searchQuery || selectedLevel !== "all"
                ? t("risks.no_risks_filter_desc")
                : t("risks.no_risks_desc")}
              action={!searchQuery && selectedLevel === "all" && (
                <Button onClick={() => setShowAddRisk(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t("risks.add_first")}
                </Button>
              )}
            />
          )}
        </div>
      </ScrollArea>

      {/* Add Asset Dialog */}
      <Dialog open={showAddAsset} onOpenChange={setShowAddAsset}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("risks.new_asset_title")}</DialogTitle>
            <DialogDescription>{t("risks.new_asset_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="asset-name">{t("risks.asset_name")} *</Label>
              <Input
                id="asset-name"
                value={newAsset.name}
                onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })}
                placeholder={t("risks.ph_asset_name")}
                data-testid="asset-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="asset-type">{t("risks.asset_type")}</Label>
              <Select
                value={newAsset.asset_type}
                onValueChange={(v) => setNewAsset({ ...newAsset, asset_type: v })}
              >
                <SelectTrigger data-testid="asset-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hardware">{t("risks.asset_type.hardware")}</SelectItem>
                  <SelectItem value="software">{t("risks.asset_type.software")}</SelectItem>
                  <SelectItem value="data">{t("risks.asset_type.data")}</SelectItem>
                  <SelectItem value="service">{t("risks.asset_type.service")}</SelectItem>
                  <SelectItem value="people">{t("risks.asset_type.people")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="asset-desc">{t("risks.asset_desc")}</Label>
              <Textarea
                id="asset-desc"
                value={newAsset.description}
                onChange={(e) => setNewAsset({ ...newAsset, description: e.target.value })}
                placeholder={t("risks.ph_asset_desc")}
                data-testid="asset-desc-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t("risks.confidentiality")}: {newAsset.confidentiality}</Label>
                <Slider
                  value={[newAsset.confidentiality]}
                  onValueChange={([v]) => setNewAsset({ ...newAsset, confidentiality: v })}
                  min={1}
                  max={5}
                  step={1}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("risks.integrity")}: {newAsset.integrity}</Label>
                <Slider
                  value={[newAsset.integrity]}
                  onValueChange={([v]) => setNewAsset({ ...newAsset, integrity: v })}
                  min={1}
                  max={5}
                  step={1}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("risks.availability")}: {newAsset.availability}</Label>
                <Slider
                  value={[newAsset.availability]}
                  onValueChange={([v]) => setNewAsset({ ...newAsset, availability: v })}
                  min={1}
                  max={5}
                  step={1}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAsset(false)}>{t("cancel")}</Button>
            <Button onClick={handleAddAsset} data-testid="save-asset-btn">{t("risks.add_asset_btn")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Risk Sheet */}
      <Sheet open={showAddRisk} onOpenChange={setShowAddRisk}>
        <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col gap-0 p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <SheetTitle>{t("risks.new_risk_title")}</SheetTitle>
            <SheetDescription>{t("risks.new_risk_desc")}</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-4">
            {/* Risk Catalog quick-load — pre-populates category, source, description
                and existing_controls from the IT framework risk catalog seeds. */}
            {catalogItems.length > 0 && (
              <div className="space-y-2 pb-3 border-b border-border">
                <Label>{t("risks.load_from_catalog")}</Label>
                <Select onValueChange={applyTemplate}>
                  <SelectTrigger data-testid="risk-catalog-select">
                    <SelectValue placeholder={t("risks.catalog_placeholder")} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {catalogItems.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        <span className="text-xs text-muted-foreground mr-1">
                          [{item._framework_short}]
                        </span>
                        {item.category} — {(item.description || "").slice(0, 55)}{item.description?.length > 55 ? "…" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {catalogTemplateApplied && (
                  <Badge variant="outline" className="text-xs text-primary border-primary/50">
                    {t("risks.template_applied")}
                  </Badge>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="risk-title">{t("risks.title_label")} *</Label>
              <Input
                id="risk-title"
                value={newRisk.title}
                onChange={(e) => setNewRisk({ ...newRisk, title: e.target.value })}
                placeholder={t("risks.ph_risk_title")}
                data-testid="risk-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="risk-asset">{t("risks.related_asset")} *</Label>
              <Select
                value={newRisk.asset_id}
                onValueChange={(v) => setNewRisk({ ...newRisk, asset_id: v })}
              >
                <SelectTrigger data-testid="risk-asset-select">
                  <SelectValue placeholder={t("risks.ph_select_asset")} />
                </SelectTrigger>
                <SelectContent>
                  {assets.map(asset => (
                    <SelectItem key={asset.asset_id} value={asset.asset_id}>
                      {asset.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="risk-threat">{t("risks.threat_label")}</Label>
              <Select
                value={newRisk.threat_id}
                onValueChange={(v) => setNewRisk({ ...newRisk, threat_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("risks.ph_select_threat")} />
                </SelectTrigger>
                <SelectContent>
                  {threats.map(threat => (
                    <SelectItem key={threat.threat_id} value={threat.threat_id}>
                      {threat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="risk-desc">{t("description")}</Label>
              <Textarea
                id="risk-desc"
                value={newRisk.description}
                onChange={(e) => setNewRisk({ ...newRisk, description: e.target.value })}
                placeholder={t("risks.ph_risk_desc")}
                data-testid="risk-desc-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("risks.likelihood")}: {newRisk.likelihood}</Label>
                <Slider
                  value={[newRisk.likelihood]}
                  onValueChange={([v]) => setNewRisk({ ...newRisk, likelihood: v })}
                  min={1}
                  max={5}
                  step={1}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("risks.impact")}: {newRisk.impact}</Label>
                <Slider
                  value={[newRisk.impact]}
                  onValueChange={([v]) => setNewRisk({ ...newRisk, impact: v })}
                  min={1}
                  max={5}
                  step={1}
                />
              </div>
            </div>
            <div className="p-3 rounded-lg bg-muted text-center">
              <span className="text-sm text-muted-foreground">{t("risks.score_label")} </span>
              <span className="text-lg font-bold">{newRisk.likelihood * newRisk.impact}</span>
            </div>
            <div className="space-y-2">
              <Label>{t("risks.treatment")}</Label>
              <Select
                value={newRisk.treatment}
                onValueChange={(v) => setNewRisk({ ...newRisk, treatment: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mitigate">{t("risks.mitigate")}</SelectItem>
                  <SelectItem value="accept">{t("risks.accept")}</SelectItem>
                  <SelectItem value="transfer">{t("risks.transfer")}</SelectItem>
                  <SelectItem value="avoid">{t("risks.avoid")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="risk-owner">{t("risks.owner")}</Label>
              <Input
                id="risk-owner"
                value={newRisk.owner}
                onChange={(e) => setNewRisk({ ...newRisk, owner: e.target.value })}
                placeholder="e.g., IT Security Team"
              />
            </div>

            {/* ISO 31000 / ISO 27005 Methodology */}
            <div className="pt-2 border-t border-border">
              <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wider">{t("risks.iso_assessment")}</p>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("risks.risk_category")}</Label>
                    <Select value={newRisk.risk_category} onValueChange={(v) => setNewRisk({ ...newRisk, risk_category: v })}>
                      <SelectTrigger data-testid="risk-category-select"><SelectValue placeholder={t("risks.ph_select_category")} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="strategic">{t("risks.cat.strategic")}</SelectItem>
                        <SelectItem value="operational">{t("risks.cat.operational")}</SelectItem>
                        <SelectItem value="compliance">{t("risks.cat.compliance")}</SelectItem>
                        <SelectItem value="financial">{t("risks.cat.financial")}</SelectItem>
                        <SelectItem value="reputational">{t("risks.cat.reputational")}</SelectItem>
                        <SelectItem value="technological">{t("risks.cat.technological")}</SelectItem>
                        <SelectItem value="natural">{t("risks.cat.natural")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t("risks.risk_source")}</Label>
                    <Select value={newRisk.risk_source} onValueChange={(v) => setNewRisk({ ...newRisk, risk_source: v })}>
                      <SelectTrigger><SelectValue placeholder={t("risks.ph_select_source")} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="internal">{t("risks.source.internal")}</SelectItem>
                        <SelectItem value="external">{t("risks.source.external")}</SelectItem>
                        <SelectItem value="environmental">{t("risks.source.environmental")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">{t("risks.cia_section")}</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">{t("risks.confidentiality")}: {newRisk.impact_confidentiality}</Label>
                      <Slider value={[newRisk.impact_confidentiality]} onValueChange={([v]) => setNewRisk({ ...newRisk, impact_confidentiality: v })} min={1} max={5} step={1} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">{t("risks.integrity")}: {newRisk.impact_integrity}</Label>
                      <Slider value={[newRisk.impact_integrity]} onValueChange={([v]) => setNewRisk({ ...newRisk, impact_integrity: v })} min={1} max={5} step={1} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">{t("risks.availability")}: {newRisk.impact_availability}</Label>
                      <Slider value={[newRisk.impact_availability]} onValueChange={([v]) => setNewRisk({ ...newRisk, impact_availability: v })} min={1} max={5} step={1} />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t("risks.existing_controls")}</Label>
                  <Textarea value={newRisk.existing_controls || ""} onChange={(e) => setNewRisk({ ...newRisk, existing_controls: e.target.value })}
                    placeholder={t("risks.ph_existing_controls")} rows={2} />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("risks.residual_likelihood")}: {newRisk.residual_likelihood || "-"}</Label>
                    <Slider value={[newRisk.residual_likelihood || newRisk.likelihood]} onValueChange={([v]) => setNewRisk({ ...newRisk, residual_likelihood: v })} min={1} max={5} step={1} />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("risks.residual_impact")}: {newRisk.residual_impact || "-"}</Label>
                    <Slider value={[newRisk.residual_impact || newRisk.impact]} onValueChange={([v]) => setNewRisk({ ...newRisk, residual_impact: v })} min={1} max={5} step={1} />
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-muted/50 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t("risks.residual_risk_score")}:</span>
                  <span className="text-lg font-bold">{(newRisk.residual_likelihood || newRisk.likelihood) * (newRisk.residual_impact || newRisk.impact)}</span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("risks.appetite")}</Label>
                    <Select value={newRisk.risk_appetite} onValueChange={(v) => setNewRisk({ ...newRisk, risk_appetite: v })}>
                      <SelectTrigger><SelectValue placeholder={t("risks.ph_appetite")} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">{t("low")}</SelectItem>
                        <SelectItem value="moderate">{t("moderate")}</SelectItem>
                        <SelectItem value="high">{t("high")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t("risks.review_freq")}</Label>
                    <Select value={newRisk.review_frequency} onValueChange={(v) => setNewRisk({ ...newRisk, review_frequency: v })}>
                      <SelectTrigger><SelectValue placeholder={t("risks.ph_frequency")} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">{t("risks.monthly")}</SelectItem>
                        <SelectItem value="quarterly">{t("risks.quarterly")}</SelectItem>
                        <SelectItem value="semi_annual">{t("risks.semi_annual")}</SelectItem>
                        <SelectItem value="annual">{t("risks.annual")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {orgFrameworks.length > 0 && (
                  <div className="space-y-2 mt-3">
                    <Label>{t("risks.relevant_frameworks")}</Label>
                    <div className="flex flex-wrap gap-2">
                      {orgFrameworks.map((f) => {
                        const selected = (newRisk.relevant_frameworks || []).includes(f.id);
                        return (
                          <button
                            key={f.id}
                            type="button"
                            onClick={() => setNewRisk({
                              ...newRisk,
                              relevant_frameworks: selected
                                ? newRisk.relevant_frameworks.filter((x) => x !== f.id)
                                : [...(newRisk.relevant_frameworks || []), f.id],
                            })}
                            className="px-2.5 py-1 rounded-full text-xs border transition-colors"
                            style={{
                              borderColor: f.color,
                              backgroundColor: selected ? f.color : "transparent",
                              color: selected ? "#fff" : f.color,
                            }}
                          >
                            {f.short}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          </div>
          <SheetFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => { setShowAddRisk(false); resetNewRisk(); }}>{t("cancel")}</Button>
            <Button onClick={handleAddRisk} data-testid="save-risk-btn">{t("risks.add_btn")}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Edit Risk Sheet */}
      <Sheet open={!!editingRisk} onOpenChange={() => setEditingRisk(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col gap-0 p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <SheetTitle>{t("edit")} {t("risks.title_label")}</SheetTitle>
          </SheetHeader>
          {editingRisk && (
            <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t("risks.title_label")}</Label>
                <Input
                  value={editingRisk.title}
                  onChange={(e) => setEditingRisk({ ...editingRisk, title: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("description")}</Label>
                <Textarea
                  value={editingRisk.description || ""}
                  onChange={(e) => setEditingRisk({ ...editingRisk, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("risks.likelihood")}: {editingRisk.likelihood}</Label>
                  <Slider
                    value={[editingRisk.likelihood]}
                    onValueChange={([v]) => setEditingRisk({ ...editingRisk, likelihood: v })}
                    min={1}
                    max={5}
                    step={1}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("risks.impact")}: {editingRisk.impact}</Label>
                  <Slider
                    value={[editingRisk.impact]}
                    onValueChange={([v]) => setEditingRisk({ ...editingRisk, impact: v })}
                    min={1}
                    max={5}
                    step={1}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("risks.treatment")}</Label>
                <Select
                  value={editingRisk.treatment}
                  onValueChange={(v) => setEditingRisk({ ...editingRisk, treatment: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mitigate">{t("risks.mitigate")}</SelectItem>
                    <SelectItem value="accept">{t("risks.accept")}</SelectItem>
                    <SelectItem value="transfer">{t("risks.transfer")}</SelectItem>
                    <SelectItem value="avoid">{t("risks.avoid")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("risks.owner")}</Label>
                <Input
                  value={editingRisk.owner || ""}
                  onChange={(e) => setEditingRisk({ ...editingRisk, owner: e.target.value })}
                />
              </div>
            </div>
            </div>
          )}
          <SheetFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => setEditingRisk(null)}>{t("cancel")}</Button>
            <Button onClick={handleUpdateRisk}>{t("risks.save_changes")}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default RiskRegister;
